import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from "react";
import { useSession } from "../../../lib/auth";
import { bucketApi } from "../api/bucket.api";
import { clearBucket, readBucket, writeBucket } from "./bucket-storage";
import {
  bucketReducer,
  initialState,
  isSameLine,
  type BucketAction,
} from "./bucket-reducer";
import type { BucketAddInput, BucketItemSnapshot } from "./bucket-types";

interface BucketContextValue {
  items: BucketItemSnapshot[];
  hydrated: boolean;
  syncing: boolean;
  count: number;
  total: number;
  /**
   * Add a new line. For authed users this hits the server (which recomputes
   * the priceSnapshot); for anon users it lands in localStorage with a
   * `local-<uuid>` id and the locally-computed priceSnapshot.
   */
  add: (input: BucketAddInput) => Promise<BucketItemSnapshot | null>;
  remove: (id: string) => Promise<void>;
  patch: (id: string, patch: Partial<BucketItemSnapshot>) => Promise<void>;
  clearLocal: () => void;
  refresh: () => Promise<void>;
}

const BucketContext = createContext<BucketContextValue | null>(null);

function makeLocalId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `local-${crypto.randomUUID()}`;
  }
  return `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function localFromInput(input: BucketAddInput): BucketItemSnapshot {
  return {
    id: makeLocalId(),
    activityId: input.activityId,
    activitySlug: input.activitySlug,
    packageId: input.packageId,
    date: input.date,
    dateEnd: input.dateEnd ?? null,
    time: input.time ?? null,
    adults: input.adults,
    children: input.children,
    infants: input.infants,
    hours: input.hours ?? null,
    nights: null,
    // Units booked on per-vehicle pricing. Dropping this made an anonymous
    // basket line for 3 quads render as one.
    groups: input.groups ?? null,
    transport: input.transport,
    // Dropping this lost the outside-the-city pickup surcharge the basket had
    // already quoted, so the line re-priced lower on sync.
    transportOutside: input.transportOutside ?? false,
    notes: input.notes ?? null,
    thumbnail: input.thumbnail ?? null,
    title: input.title,
    cityName: input.cityName,
    typePrice: input.typePrice,
    maxGuests: input.maxGuests ?? null,
    includedGuests: input.includedGuests ?? null,
    pricing: input.pricing,
    priceSnapshot: input.priceSnapshot,
    addedAt: new Date().toISOString(),
  };
}

function snapshotToAddInput(it: BucketItemSnapshot): BucketAddInput {
  return {
    activityId: it.activityId,
    activitySlug: it.activitySlug,
    packageId: it.packageId,
    date: it.date,
    dateEnd: it.dateEnd ?? null,
    time: it.time ?? null,
    adults: it.adults,
    children: it.children,
    infants: it.infants,
    hours: it.hours ?? null,
    groups: it.groups ?? null,
    transport: it.transport,
    transportOutside: it.transportOutside ?? false,
    notes: it.notes ?? null,
    thumbnail: it.thumbnail ?? null,
    title: it.title,
    cityName: it.cityName,
    typePrice: it.typePrice,
    maxGuests: it.maxGuests ?? null,
    includedGuests: it.includedGuests ?? null,
    pricing: it.pricing,
    priceSnapshot: it.priceSnapshot,
  };
}

interface SessionShape {
  data?: { user?: { id?: string; email?: string } } | null;
  isPending?: boolean;
}

export function BucketProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(bucketReducer, initialState);
  const session = useSession() as SessionShape;
  const sessionUserId = session?.data?.user?.id ?? null;
  const lastSyncedUserRef = useRef<string | null>(null);

  // Persist to localStorage on every change once hydrated.
  useEffect(() => {
    if (!state.hydrated) return;
    writeBucket(state.items);
  }, [state.items, state.hydrated]);

  // Initial hydrate: anon load from localStorage; authed load from server.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!sessionUserId) {
        if (!cancelled) {
          dispatch({ type: "hydrate", items: readBucket() });
        }
        return;
      }
      // Authed: pick up local items, sync to server, then load fresh server list.
      const local = readBucket();
      try {
        if (lastSyncedUserRef.current !== sessionUserId) {
          dispatch({ type: "syncing", syncing: true });
          const merged = local.length
            ? await bucketApi.sync(local.map(snapshotToAddInput))
            : await bucketApi.list();
          if (cancelled) return;
          dispatch({ type: "hydrate", items: merged });
          lastSyncedUserRef.current = sessionUserId;
          if (local.length) clearBucket();
        } else {
          const fresh = await bucketApi.list();
          if (cancelled) return;
          dispatch({ type: "hydrate", items: fresh });
        }
      } catch {
        if (!cancelled) {
          dispatch({ type: "hydrate", items: local });
        }
      } finally {
        if (!cancelled) dispatch({ type: "syncing", syncing: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionUserId]);

  const add = useCallback<BucketContextValue["add"]>(
    async (input) => {
      if (sessionUserId) {
        try {
          const server = await bucketApi.add(input);
          if (!server) return null;
          dispatch({ type: "upsert-server", item: server });
          return server;
        } catch {
          // Fall through to local mode on failure so the UI never silently drops.
        }
      }
      const existingTwin = state.items.find((it) => isSameLine(it, input));
      const local = existingTwin
        ? { ...existingTwin, ...localFromInput(input), id: existingTwin.id }
        : localFromInput(input);
      const action: BucketAction = existingTwin
        ? { type: "patch", id: existingTwin.id, patch: local }
        : { type: "add-local", item: local };
      dispatch(action);
      return local;
    },
    [sessionUserId, state.items],
  );

  const remove = useCallback<BucketContextValue["remove"]>(
    async (id) => {
      if (sessionUserId && !id.startsWith("local-")) {
        try {
          await bucketApi.remove(id);
        } catch {
          // Best-effort: still drop locally so the user isn't stuck.
        }
      }
      dispatch({ type: "remove", id });
    },
    [sessionUserId],
  );

  const patch = useCallback<BucketContextValue["patch"]>(
    async (id, patchValues) => {
      dispatch({ type: "patch", id, patch: patchValues });
      if (sessionUserId && !id.startsWith("local-")) {
        try {
          const server = await bucketApi.update(id, {
            packageId: patchValues.packageId,
            date: patchValues.date,
            dateEnd: patchValues.dateEnd ?? undefined,
            time: patchValues.time ?? undefined,
            adults: patchValues.adults,
            children: patchValues.children,
            infants: patchValues.infants,
            hours: patchValues.hours ?? undefined,
            groups: patchValues.groups ?? undefined,
            transport: patchValues.transport,
            transportOutside: patchValues.transportOutside,
            notes: patchValues.notes ?? undefined,
          });
          // Adopt the server's recomputed line so the displayed total is the
          // one we will actually charge. Patch (not upsert) so the edited line
          // keeps its place in the list.
          if (server) dispatch({ type: "patch", id, patch: server });
        } catch {
          // Already optimistically updated locally.
        }
      }
    },
    [sessionUserId],
  );

  const clearLocal = useCallback(() => {
    dispatch({ type: "clear" });
    clearBucket();
  }, []);

  const refresh = useCallback(async () => {
    if (!sessionUserId) {
      dispatch({ type: "hydrate", items: readBucket() });
      return;
    }
    try {
      const fresh = await bucketApi.list();
      dispatch({ type: "set", items: fresh });
    } catch {
      // Keep current state.
    }
  }, [sessionUserId]);

  const total = useMemo(
    () => state.items.reduce((acc, it) => acc + (it.priceSnapshot ?? 0), 0),
    [state.items],
  );

  const value = useMemo<BucketContextValue>(
    () => ({
      items: state.items,
      hydrated: state.hydrated,
      syncing: state.syncing,
      count: state.items.length,
      total,
      add,
      remove,
      patch,
      clearLocal,
      refresh,
    }),
    [state.items, state.hydrated, state.syncing, total, add, remove, patch, clearLocal, refresh],
  );

  return <BucketContext.Provider value={value}>{children}</BucketContext.Provider>;
}

export function useBucket(): BucketContextValue {
  const ctx = useContext(BucketContext);
  if (!ctx) {
    throw new Error("useBucket must be used inside <BucketProvider>");
  }
  return ctx;
}

/**
 * The bucket where it is a nicety, not the feature.
 *
 * `useBucket` throws by design: a bucket page without a provider is a bug that
 * should be loud. But the stays confirmation screen also touches the bucket,
 * to turn checkout add-ons into real lines, and that screen is shown to
 * someone whose card has ALREADY been charged. Crashing it over a missing
 * provider would replace "your room is booked" with a blank page for the sake
 * of an optional extra. Callers here degrade instead.
 */
export function useBucketOptional(): BucketContextValue | null {
  return useContext(BucketContext);
}
